/**
 * Dokumente — every file in the owner's portfolio, in one place (COL-528).
 *
 * Documents used to live inside a property page, which meant an owner looking
 * for one contract had to remember which building it was filed under. This is
 * the same rows, read across the portfolio, with search and a drill-down.
 *
 * It is the ONLY documents list in the owner portal (COL-628): the property
 * page's own Documents tab is gone. So instead of one chip per filing level,
 * the pills walk down the building: Alle · Allgemein · Objektbezogen ▾ (unit),
 * then Wohnungsbezogen · Zimmer ▾, then Zimmerbezogen · Mieter ▾. Each row of
 * pills appears under the one above once something is picked there. The
 * placing logic lives in `@/lib/owner-document-drilldown`.
 *
 * A property-scoped row (coliviq files them, e.g. an Energieausweis) has no
 * unit, so it shows under Wohnungsbezogen of every unit in its building.
 *
 * The owner's own uploads keep the rename and remove (with a reason) that the
 * property tab offered; the RPC's uploaded_by_me says which rows those are.
 */
import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Pencil, Search, Trash2, X } from "lucide-react";
import { AdminV2Layout } from "@/components/admin/v2/AdminV2Layout";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { useToast } from "@/hooks/use-toast";
import {
  useRenameApartmentDocument,
  useSoftDeleteApartmentDocument,
} from "@/hooks/v2/useApartmentDocuments";
import { openPendingDocumentTab } from "@/lib/open-document-tab";
import { supabase } from "@/lib/supabase";
import { resolveTenantDocumentUrl } from "@/lib/tenant-document-url";
import { localToday } from "@/lib/tenant-timeline";
import {
  documentsAtPath,
  roomOptions,
  ROOT_PATH,
  tenancyOptions,
  unitOptions,
  type DocumentPath,
  type TenancyOption,
  type TenancyPhase,
  type UnitOption,
} from "@/lib/owner-document-drilldown";
import {
  useOwnerPortfolioDocuments,
  ownerPortfolioDocumentsKey,
  documentHaystack,
  matchesDocumentSearch,
  type OwnerDocumentLevel,
  type OwnerPortfolioDocument,
} from "@/hooks/owner/useOwnerPortfolioDocuments";
import { OwnerAddDocumentDialog } from "@/components/owner/OwnerAddDocumentDialog";
import { Pill, PillMenu, SubRow } from "@/components/owner/OwnerDocumentPills";
import { cn } from "@/lib/utils";

const C = {
  petrol: "#0F4C4A",
  hairline: "#ECE7DD",
  muted: "#7A8E8C",
  body: "#47605E",
  tint: "#E3EFED",
  sand: "#FBF9F5",
  teal: "#2FB3AA",
} as const;

/** The bucket both document tables store into. */
const DOC_BUCKET = "contracts";

export default function OwnerDocumentsPage() {
  const { t, language } = useLanguage();
  const locale = language === "de" ? "de-DE" : "en-GB";
  const {
    data: documents = [],
    isLoading,
    isError,
  } = useOwnerPortfolioDocuments();
  const [search, setSearch] = useState("");
  const [path, setPath] = useState<DocumentPath>(ROOT_PATH);
  const [addOpen, setAddOpen] = useState(false);
  const [openError, setOpenError] = useState<string | null>(null);
  const { user } = useAuth();
  const qc = useQueryClient();
  const { toast } = useToast();
  const renameMut = useRenameApartmentDocument();
  const deleteMut = useSoftDeleteApartmentDocument();
  const [renaming, setRenaming] = useState<OwnerPortfolioDocument | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [deleting, setDeleting] = useState<OwnerPortfolioDocument | null>(null);
  const [deleteReason, setDeleteReason] = useState("");

  // Rename and remove replace the property Documents tab's controls: only the
  // viewer's own apartment_documents uploads made from the owner side. That is
  // exactly what the owner UPDATE policy allows (source = 'owner' AND
  // uploaded_by = auth.uid()); anything wider would offer an edit RLS turns
  // into a silent zero-row no-op.
  const canEdit = (d: OwnerPortfolioDocument) =>
    d.origin === "apartment" &&
    d.source === "owner" &&
    d.uploaded_by_me === true;

  // The hooks take the row's ids; property_id is non-null on every
  // apartment-origin row. They refresh the per-unit lists, this page reads
  // the portfolio RPC, so refresh that too.
  const docRef = (d: OwnerPortfolioDocument) => ({
    id: d.id,
    apartment_id: d.apartment_id ?? null,
    property_id: d.property_id ?? 0,
  });
  const refresh = () =>
    qc.invalidateQueries({ queryKey: ownerPortfolioDocumentsKey(user?.id) });

  function submitRename() {
    if (!renaming || !renameValue.trim()) return;
    renameMut.mutate(
      { doc: docRef(renaming), title: renameValue },
      {
        onSuccess: () => {
          setRenaming(null);
          void refresh();
        },
        onError: (err) =>
          toast({
            title: t("owner.docs.renameFailed", "Umbenennen fehlgeschlagen"),
            description: err instanceof Error ? err.message : undefined,
            variant: "destructive",
          }),
      }
    );
  }

  function submitDelete() {
    if (!deleting || !deleteReason.trim()) return;
    deleteMut.mutate(
      { doc: docRef(deleting), reason: deleteReason },
      {
        onSuccess: () => {
          setDeleting(null);
          setDeleteReason("");
          void refresh();
        },
        onError: (err) =>
          toast({
            title: t("owner.docs.deleteFailed", "Entfernen fehlgeschlagen"),
            description: err instanceof Error ? err.message : undefined,
            variant: "destructive",
          }),
      }
    );
  }

  const levelLabel = (l: OwnerDocumentLevel) =>
    ({
      general: t("owner.docs.levelGeneral", "Allgemein"),
      property: t("owner.docs.levelProperty", "Immobilie"),
      apartment: t("owner.docs.levelApartment", "Wohnung"),
      room: t("owner.docs.levelRoom", "Zimmer"),
      tenant: t("owner.docs.levelTenant", "Mieter"),
    })[l];

  const sourceLabel = (d: OwnerPortfolioDocument) =>
    d.source === "owner"
      ? t("owner.docs.yourUpload", "Ihr Upload")
      : d.source === "tenant"
        ? t("owner.docs.fromTenant", "Vom Mieter hochgeladen")
        : t("owner.docs.fromColiviq", "Von coliviq");

  const dateLabel = (iso: string) =>
    new Date(iso).toLocaleDateString(locale, {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });

  const dayLabel = (ymd: string | null) =>
    ymd
      ? new Date(`${ymd.slice(0, 10)}T12:00:00`).toLocaleDateString(locale, {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        })
      : "—";

  // An open-ended tenancy runs "until today" only once it has started; one
  // that has not reads "from <move-in>".
  const spanLabel = (x: TenancyOption) =>
    x.moveOut
      ? `${dayLabel(x.moveIn)} – ${dayLabel(x.moveOut)}`
      : x.phase === "future"
        ? t("owner.docs.fromDate", "ab {date}").replace(
            "{date}",
            dayLabel(x.moveIn)
          )
        : `${dayLabel(x.moveIn)} – ${t("owner.docs.today", "heute")}`;

  const unitLabel = (u: UnitOption) =>
    [
      u.propertyTitle ?? t("owner.docs.levelProperty", "Immobilie"),
      u.apartmentLabel,
    ]
      .filter(Boolean)
      .join(", ");

  const phaseLabel = (p: TenancyPhase) =>
    ({
      current: t("owner.docs.phaseCurrent", "Aktiv"),
      notice: t("owner.docs.phaseNotice", "Gekündigt"),
      future: t("owner.docs.phaseFuture", "Bevorstehend"),
      past: t("owner.docs.phasePast", "Ausgezogen"),
    })[p];

  const today = localToday();
  const units = useMemo(() => unitOptions(documents), [documents]);
  const unit = units.find((u) => u.key === path.unit) ?? null;
  const rooms = useMemo(
    () => (path.unit ? roomOptions(documents, path.unit) : []),
    [documents, path.unit]
  );
  const room = rooms.find((r) => r.id === path.room) ?? null;
  const tenancies = useMemo(
    () =>
      path.room != null ? tenancyOptions(documents, path.room, today) : [],
    [documents, path.room, today]
  );
  const tenancy = tenancies.find((x) => x.bookingId === path.tenancy) ?? null;

  const rows = useMemo(() => {
    return documentsAtPath(documents, path, units).filter((d) => {
      if (!search.trim()) return true;
      return matchesDocumentSearch(
        documentHaystack(
          d,
          levelLabel(d.level),
          sourceLabel(d),
          dateLabel(d.created_at)
        ),
        search
      );
    });
    // levelLabel/sourceLabel/dateLabel are pure given t + locale.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documents, path, units, search, t, locale]);

  async function open(d: OwnerPortfolioDocument) {
    // The tab is opened inside the click, BEFORE the signing round trip: a
    // window.open() after an await is a popup the browser blocks, and the
    // owner portal's protocol links already learned that the hard way.
    const tab = openPendingDocumentTab();
    // Clear the previous failure: without this, one unopenable document leaves
    // an error standing over every later successful click.
    setOpenError(null);

    // A tenant document does not live in `contracts`: its file_url may be a
    // full URL, a bucket-qualified path, or a bare path in `tenant-documents`.
    // resolveTenantDocumentUrl is the one place that knows which — signing all
    // three against one bucket made every tenant document fail to open.
    const url =
      d.origin === "tenant"
        ? await resolveTenantDocumentUrl(d.file_url)
        : ((
            await supabase.storage
              .from(DOC_BUCKET)
              .createSignedUrl(d.file_url, 60)
          ).data?.signedUrl ?? null);

    if (!url) {
      tab.cancel();
      setOpenError(
        t(
          "owner.docs.openFailed",
          "Dieses Dokument konnte nicht geöffnet werden."
        )
      );
      return;
    }
    tab.navigate(url);
  }

  return (
    <AdminV2Layout title={t("owner.documents.title", "Dokumente")}>
      <div className="mb-[18px] flex flex-wrap items-center justify-end gap-3">
        <button
          type="button"
          onClick={() => setAddOpen(true)}
          className="h-[34px] rounded-full px-4 text-[13px] font-semibold text-white transition-colors"
          style={{ background: C.petrol }}
        >
          + {t("owner.docs.addDocument", "Dokument hinzufügen")}
        </button>
      </div>

      <div className="relative mb-3.5">
        <Search
          className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2"
          style={{ color: C.muted }}
          aria-hidden
        />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t(
            "owner.docs.searchPlaceholder",
            "Dateiname, Mieter, Immobilie, Wohnung, Zimmer, Typ oder Datum…"
          )}
          aria-label={t("owner.docs.searchLabel", "Dokumente durchsuchen")}
          className="h-11 w-full rounded-xl border pl-[42px] pr-10 text-sm outline-none transition-colors focus:bg-white"
          style={{
            borderColor: C.hairline,
            background: C.sand,
            color: "#1A2B2A",
          }}
        />
        {search && (
          <button
            type="button"
            onClick={() => setSearch("")}
            aria-label={t("owner.docs.clearSearch", "Suche löschen")}
            className="absolute right-2.5 top-1/2 flex h-[26px] w-[26px] -translate-y-1/2 items-center justify-center rounded-full"
            style={{ background: C.hairline, color: C.body }}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      <div className="mb-3.5 flex flex-col gap-2">
        <div className="flex flex-wrap gap-1.5">
          <Pill active={path.top === "all"} onClick={() => setPath(ROOT_PATH)}>
            {t("owner.docs.chipAll", "Alle")}
          </Pill>
          <Pill
            active={path.top === "general"}
            onClick={() => setPath({ ...ROOT_PATH, top: "general" })}
          >
            {t("owner.docs.levelGeneral", "Allgemein")}
          </Pill>
          <PillMenu
            active={path.top === "object"}
            label={
              unit
                ? unitLabel(unit)
                : t("owner.docs.chipObject", "Objektbezogen")
            }
            heading={t("owner.docs.chooseUnit", "Wohnung wählen")}
          >
            <DropdownMenuItem
              onSelect={() => setPath({ ...ROOT_PATH, top: "object" })}
            >
              {t("owner.docs.allObjects", "Alle Objekte")}
            </DropdownMenuItem>
            {units.length > 0 && <DropdownMenuSeparator />}
            {units.map((u) => (
              <DropdownMenuItem
                key={u.key}
                onSelect={() =>
                  setPath({
                    top: "object",
                    unit: u.key,
                    room: null,
                    tenancy: null,
                  })
                }
                className={cn(u.key === path.unit && "font-semibold")}
              >
                {unitLabel(u)}
              </DropdownMenuItem>
            ))}
          </PillMenu>
        </div>

        {unit && (
          <SubRow>
            <Pill
              soft
              active={path.room == null}
              onClick={() => setPath({ ...path, room: null, tenancy: null })}
            >
              {t("owner.docs.chipUnitScope", "Wohnungsbezogen")}
            </Pill>
            {rooms.length > 0 && (
              <PillMenu
                soft
                active={room != null}
                label={
                  room
                    ? (room.title ?? t("owner.docs.levelRoom", "Zimmer"))
                    : t("owner.docs.levelRoom", "Zimmer")
                }
                heading={t("owner.docs.chooseRoom", "Zimmer wählen")}
              >
                {rooms.map((r) => (
                  <DropdownMenuItem
                    key={r.id}
                    onSelect={() =>
                      setPath({ ...path, room: r.id, tenancy: null })
                    }
                    className={cn(r.id === path.room && "font-semibold")}
                  >
                    {r.title ?? t("owner.docs.levelRoom", "Zimmer")}
                  </DropdownMenuItem>
                ))}
              </PillMenu>
            )}
          </SubRow>
        )}

        {unit && room && (
          <SubRow key={room.id} indent>
            <Pill
              soft
              active={path.tenancy == null}
              onClick={() => setPath({ ...path, tenancy: null })}
            >
              {t("owner.docs.chipRoomScope", "Zimmerbezogen")}
            </Pill>
            {tenancies.length > 0 && (
              <PillMenu
                soft
                active={tenancy != null}
                label={
                  tenancy
                    ? (tenancy.name ?? t("owner.docs.levelTenant", "Mieter"))
                    : t("owner.docs.levelTenant", "Mieter")
                }
                heading={t(
                  "owner.docs.tenantsNewestFirst",
                  "Mieter · neueste zuerst"
                )}
              >
                {tenancies.map((x) => (
                  <DropdownMenuItem
                    key={x.bookingId}
                    onSelect={() => setPath({ ...path, tenancy: x.bookingId })}
                    className={cn(
                      "items-start gap-2.5",
                      x.bookingId === path.tenancy && "font-semibold"
                    )}
                  >
                    <PhaseDot phase={x.phase} />
                    <span className="min-w-0">
                      <span className="block truncate">
                        {x.name ?? t("owner.docs.levelTenant", "Mieter")}
                      </span>
                      <span
                        className="block text-[11.5px] font-normal"
                        style={{ color: C.muted }}
                      >
                        {phaseLabel(x.phase)} · {spanLabel(x)}
                      </span>
                    </span>
                  </DropdownMenuItem>
                ))}
              </PillMenu>
            )}
          </SubRow>
        )}
      </div>

      {unit && room && tenancy && (
        <div
          key={tenancy.bookingId}
          className="mb-3 flex items-center gap-3 rounded-xl border px-3.5 py-3 animate-in fade-in slide-in-from-top-1 duration-200 motion-reduce:animate-none"
          style={{ borderColor: C.hairline, background: C.sand }}
        >
          <span
            className="flex h-9 w-9 flex-none items-center justify-center rounded-full text-[13px] font-semibold text-white"
            style={{ background: C.petrol }}
            aria-hidden
          >
            {initials(tenancy.name)}
          </span>
          <span className="min-w-0">
            <span
              className="block truncate text-[14.5px] font-semibold"
              style={{ color: C.petrol }}
            >
              {tenancy.name ?? t("owner.docs.levelTenant", "Mieter")}
            </span>
            <span className="block truncate text-xs" style={{ color: C.muted }}>
              {unitLabel(unit)} ·{" "}
              {room.title ?? t("owner.docs.levelRoom", "Zimmer")} ·{" "}
              {spanLabel(tenancy)}
            </span>
          </span>
          <span
            className="ml-auto whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold"
            style={PHASE_BADGE[tenancy.phase]}
          >
            {phaseLabel(tenancy.phase)}
          </span>
        </div>
      )}

      <div
        className="border-b pb-2 text-xs"
        style={{ borderColor: C.hairline, color: C.muted }}
      >
        {isLoading
          ? t("owner.docs.loading", "Wird geladen…")
          : t("owner.docs.count", "{n} Dokumente").replace(
              "{n}",
              String(rows.length)
            )}
      </div>

      {/* A failed read must not look like an empty archive: "no documents"
          and "we could not load your documents" are different facts, and only
          one of them is the owner's to act on. */}
      {isError && (
        <p
          className="py-9 text-center text-[13px]"
          style={{ color: "#C0714F" }}
          role="alert"
        >
          {t(
            "owner.docs.loadFailed",
            "Ihre Dokumente konnten nicht geladen werden. Bitte laden Sie die Seite neu."
          )}
        </p>
      )}

      {openError && (
        <p className="pt-3 text-xs" style={{ color: "#C0714F" }} role="alert">
          {openError}
        </p>
      )}

      {!isLoading && !isError && rows.length === 0 && (
        <p className="py-9 text-center text-[13px]" style={{ color: C.muted }}>
          {documents.length === 0
            ? t(
                "owner.docs.emptyPortfolio",
                "Noch keine Dokumente. Verträge und Protokolle erscheinen hier automatisch."
              )
            : search.trim()
              ? t(
                  "owner.docs.noMatches",
                  "Keine Dokumente passen zu dieser Suche."
                )
              : t("owner.docs.emptyLevel", "Keine Dokumente auf dieser Ebene.")}
        </p>
      )}

      <ul>
        {!isError &&
          rows.map((d) => (
            <li
              key={`${d.level}-${d.id}`}
              className="flex items-center border-b transition-colors hover:bg-[#FBF9F5]"
              style={{ borderColor: C.hairline }}
            >
              <button
                type="button"
                onClick={() => void open(d)}
                className="grid min-w-0 flex-1 grid-cols-[36px_minmax(0,1fr)_auto] items-center gap-3.5 py-3 text-left"
              >
                <span
                  className="inline-flex h-9 w-9 items-center justify-center rounded-[9px] text-[10px] font-bold"
                  style={{ background: C.tint, color: C.petrol }}
                  aria-hidden
                >
                  {fileKind(d)}
                </span>
                <span className="min-w-0">
                  <span
                    className="block truncate text-[13.5px] font-medium"
                    style={{ color: C.petrol }}
                  >
                    {d.title}
                  </span>
                  <span
                    className="mt-0.5 block truncate text-xs"
                    style={{ color: C.muted }}
                  >
                    {[
                      d.property_title,
                      levelLabel(d.level),
                      d.apartment_label,
                      d.room_title,
                      d.tenant_name,
                      sourceLabel(d),
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                </span>
                <span
                  className="whitespace-nowrap text-xs tabular-nums"
                  style={{ color: C.muted }}
                >
                  {dateLabel(d.created_at)}
                </span>
              </button>
              {canEdit(d) && (
                <span className="ml-1 flex shrink-0 items-center">
                  <button
                    type="button"
                    onClick={() => {
                      setRenaming(d);
                      setRenameValue(d.title);
                    }}
                    aria-label={t("owner.docs.rename", "Umbenennen")}
                    title={t("owner.docs.rename", "Umbenennen")}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-md transition-colors hover:bg-[#E3EFED] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2FB3AA]"
                    style={{ color: C.muted }}
                  >
                    <Pencil className="h-3.5 w-3.5" aria-hidden />
                  </button>
                  <button
                    type="button"
                    onClick={() => setDeleting(d)}
                    aria-label={t("owner.docs.delete", "Löschen")}
                    title={t("owner.docs.delete", "Löschen")}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-md transition-colors hover:bg-[#E3EFED] hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2FB3AA]"
                    style={{ color: C.muted }}
                  >
                    <Trash2 className="h-3.5 w-3.5" aria-hidden />
                  </button>
                </span>
              )}
            </li>
          ))}
      </ul>

      <OwnerAddDocumentDialog open={addOpen} onOpenChange={setAddOpen} />

      <AlertDialog
        open={!!renaming}
        onOpenChange={(o) => !o && setRenaming(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("owner.docs.renameTitle", "Dokument umbenennen")}
            </AlertDialogTitle>
            {/* Which document, for screen readers; sighted users see it in
                the field below. AlertDialog requires a description. */}
            <AlertDialogDescription className="sr-only">
              {renaming?.title}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            aria-label={t("owner.docs.renameTitle", "Dokument umbenennen")}
            autoFocus
          />
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t("owner.docs.cancel", "Abbrechen")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={submitRename}
              disabled={!renameValue.trim() || renameMut.isPending}
            >
              {t("owner.docs.save", "Speichern")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={!!deleting}
        onOpenChange={(o) => {
          if (!o) {
            setDeleting(null);
            setDeleteReason("");
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("owner.docs.deleteTitle", "Dieses Dokument entfernen?")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                "owner.docs.deleteDesc",
                "Bitte geben Sie den Grund an — so kann coliviq nachvollziehen, was sich geändert hat."
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Textarea
            value={deleteReason}
            onChange={(e) => setDeleteReason(e.target.value)}
            placeholder={t(
              "owner.docs.deleteReasonPlaceholder",
              "z. B. Falsche Datei hochgeladen, unten ersetzt."
            )}
            rows={3}
            autoFocus
          />
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t("owner.docs.cancel", "Abbrechen")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={submitDelete}
              disabled={!deleteReason.trim() || deleteMut.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t("owner.docs.deleteConfirm", "Entfernen")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminV2Layout>
  );
}

const PHASE_DOT: Record<TenancyPhase, string> = {
  current: "#2FB3AA",
  notice: "#E0A93B",
  future: "#7A8E8C",
  past: "#C9D1CF",
};

const PHASE_BADGE: Record<TenancyPhase, { background: string; color: string }> =
  {
    current: { background: "#DDF3F1", color: "#13706A" },
    notice: { background: "#FBEFD6", color: "#8A5E10" },
    future: { background: "#E3EFED", color: "#0F4C4A" },
    past: { background: "#EEF1F0", color: "#7A8E8C" },
  };

function PhaseDot({ phase }: { phase: TenancyPhase }) {
  return (
    <span
      className="mt-1.5 h-2 w-2 flex-none rounded-full"
      style={{ background: PHASE_DOT[phase] }}
      aria-hidden
    />
  );
}

function initials(name: string | null): string {
  return (name ?? "?")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");
}

/**
 * The three letters on the tile. Read from the stored name rather than the
 * content type: a file uploaded from a phone often arrives as
 * application/octet-stream, and "BIN" on every row says nothing.
 */
function fileKind(d: OwnerPortfolioDocument): string {
  const ext = (d.file_name ?? d.file_url).split(".").pop()?.toUpperCase() ?? "";
  if (ext === "JPEG") return "JPG";
  return /^[A-Z0-9]{2,4}$/.test(ext) ? ext : "DOC";
}
